import { differenceInDays } from 'date-fns';
import { distanceFromMeters } from '../common/util/converter';

// Keep in sync with backend TaskDailySummay.AT_LOT_MAX_AGE_DAYS
export const AT_LOT_MAX_AGE_DAYS = 14;

export const isAtDealerLot = (device) => {
  if (device.installerLotId == null) {
    return false;
  }
  if (!device.lastUpdate) {
    return false;
  }
  const ageMs = Date.now() - new Date(device.lastUpdate).getTime();
  return ageMs <= AT_LOT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
};

// Keep in sync with the backend airport gate in TaskDailySummay.
export const AT_AIRPORT_MIN_DAYS = 3;

// Lot types that are explicitly not an impound. Anything else - including a
// null or empty lotType on legacy rows that were never classified - counts as
// impounded, which is how the backend defaults it. Listing the exceptions
// rather than matching 'impound' keeps new lot types out of the impound bucket
// only once they are added here.
const NON_IMPOUND_LOT_TYPES = ['auction', 'junkyard', 'airport'];

// lotEntryTime is set for every kind of tracked lot, not just impounds, and the
// backend nulls it as soon as the vehicle moves. lotType may briefly keep its
// old value while lotEntryTime is null, so always gate on the timestamp first.
export const isAtLot = (device) => device.lotEntryTime != null;

export const isImpounded = (device) => isAtLot(device)
  && !NON_IMPOUND_LOT_TYPES.includes(device.lotType);

export const isAtAuction = (device) => isAtLot(device) && device.lotType === 'auction';

export const isAtJunkyard = (device) => isAtLot(device) && device.lotType === 'junkyard';

export const isAtAuctionOrJunkyard = (device) => isAtAuction(device) || isAtJunkyard(device);

// A car isn't flagged the moment it parks at an airport - the borrower may just
// be traveling. Match the daily summary email and only surface it after a
// straight AT_AIRPORT_MIN_DAYS stay. Any motion clears lotEntryTime, so the
// wait restarts on its own.
export const isAtAirport = (device) => isAtLot(device) && device.lotType === 'airport'
  && differenceInDays(new Date(), new Date(device.lotEntryTime)) >= AT_AIRPORT_MIN_DAYS;

// Single lot status for display, or null when the device isn't in a lot (or is
// at an airport but hasn't waited out AT_AIRPORT_MIN_DAYS yet).
export const getLotStatus = (device) => {
  if (isImpounded(device)) {
    return 'impound';
  }
  if (isAtAuction(device)) {
    return 'auction';
  }
  if (isAtJunkyard(device)) {
    return 'junkyard';
  }
  if (isAtAirport(device)) {
    return 'airport';
  }
  return null;
};

export const isInstallationMatching = (device, condition) => {
  if (device.disabled === true) {
    return false;
  }

  const now = new Date();
  const installTime = device.installTime ? new Date(device.installTime) : null;

  switch (condition) {
    case 'notinstalled':
      return installTime === null;
    case 'installed':
      return installTime !== null;
    case '7days':
      return installTime !== null && differenceInDays(now, installTime) <= 7;
    case '30days':
      return installTime !== null && differenceInDays(now, installTime) <= 30;
    case '60days':
      return installTime !== null && differenceInDays(now, installTime) <= 60;
    case '90days':
      return installTime !== null && differenceInDays(now, installTime) <= 90;
    default:
      return false;
  }
};

export const isLast30DaysMatching = (device, minTravel, maxTravel, distanceUnit) => {
  if (device.disabled === true) {
    return false;
  }
  if (maxTravel === 0) {
    return distanceFromMeters(device.thirtyDayDistance, distanceUnit) >= minTravel;
  }
  return distanceFromMeters(device.thirtyDayDistance, distanceUnit) >= minTravel && distanceFromMeters(device.thirtyDayDistance, distanceUnit) < maxTravel;
};

// A device is snoozed while snoozeUntil is in the future; null or a past time
// means it isn't. The server clears it when the vehicle enters a new lot.
export const isSnoozed = (device, now = Date.now()) => device.snoozeUntil != null
  && new Date(device.snoozeUntil).getTime() > now;

export const isConditionMatching = (device, condition) => {
  if (device.disabled === true) {
    return false;
  }
  // Keep in sync with the backend: a snoozed device matches only 'snoozed' and
  // is left out of every other condition, and of their counts.
  if (isSnoozed(device)) {
    return condition === 'snoozed';
  }
  switch (condition) {
    case 'recovery':
      return device.recovery;
    case 'abandoned':
      return device.abandonTime != null;
    case 'impounded':
      return isImpounded(device);
    case 'auctionjunkyard':
      return isAtAuctionOrJunkyard(device);
    case 'atairport':
      return isAtAirport(device);
    case 'outofcountry':
      return device.outOfCountryTime != null;
    case 'atlot':
      return isAtDealerLot(device);
    default:
      return false;
  }
};
